import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { List } from "immutable";
import { connect } from "react-redux";
import { compose } from "redux";
import { createStructuredSelector } from "reselect";
import LoadingBar from "../../components/LoadingBar/LoadingBar";
import injectReducer from "../../reduxFolder/injectReducer";
import booksReducer from "../../reduxFolder/reducers/books";
import { getPassports } from "../../reduxFolder/reducers/passports";
import {
  passportsSelectItems,
  passportsSelectLoading,
} from "../../reduxFolder/selectors/passportSelectors";
import { panelSelectOpened } from "../../reduxFolder/selectors/rightPanelSelectors";
import BooksPassportsList from "./components/BooksPassportsList/BooksPassportsList";
import BooksPassportsPlaceholder from "./components/BooksPassportsList/BooksPassportsPlaceholder";
import BooksSearchPassports from "./components/BooksPassportsList/BooksSearchPassports";
import AddPassportButton from "./components/BooksPassportsList/AddPassportButton";
import {
  AddAddressBlock,
  RecipientsHeading,
  StyledGrid,
  Wrapper,
} from "./styles/BookRecipients.style";

const SEARCHABLE_FIELDS = [
  "full_name",
  "serial",
  "number",
  "tax_id",
  "issued_by",
  "registration",
  "issued_date",
  "date_of_birth",
];

interface BooksPassportsProps {
  passportItems: List<any>;
  isLoading: boolean;
  getPassports: () => void;
  isPanelOpened: boolean;
}

const BooksPassports: FC<BooksPassportsProps> = React.memo(
  ({
    passportItems,
    isLoading,
    getPassports,
    isPanelOpened,
  }) => {
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
      getPassports();
    }, [getPassports]);

    const filteredItems = useMemo(() => {
      const query = searchQuery.toLowerCase().trim();
      if (!query) return passportItems;

      return passportItems.filter((item) =>
        SEARCHABLE_FIELDS.some((field) => {
          const value = item.get(field);
          return value && String(value).toLowerCase().includes(query);
        }),
      );
    }, [passportItems, searchQuery]);

    const hasItems = !!filteredItems.size;

    const handleResetClick = useCallback(() => {
      setSearchQuery("");
    }, []);

    return (
      <Wrapper>
        <StyledGrid fluid>
          <LoadingBar isLoading={isLoading} />
          <RecipientsHeading $isPanelOpened={isPanelOpened}>
            <BooksSearchPassports
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
            <AddAddressBlock>
              <AddPassportButton />
            </AddAddressBlock>
          </RecipientsHeading>

          {hasItems ? (
            <BooksPassportsList
              passportItems={filteredItems}
              disabled={isLoading}
            />
          ) : (
            !isLoading && (
              <BooksPassportsPlaceholder handleReset={handleResetClick} />
            )
          )}
        </StyledGrid>
      </Wrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    passportItems: passportsSelectItems,
    isLoading: passportsSelectLoading,
    isPanelOpened: panelSelectOpened,
  }),
  {
    getPassports,
  },
);

const withBooksReducer = injectReducer({
  key: "books",
  reducer: booksReducer,
});

const enhance = compose(
  withBooksReducer,
  withConnect,
);

export default enhance(BooksPassports);
